import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import _ from "lodash";
import styles from "./Onboarding.module.scss";
import Login from "../../components/Onboarding/Login";
import AuthService from "../../service/authService";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { JameoLogoMedium } from "../../components/JameoLogo";
import { routeFormatter } from "../../utils/Formatter";
import { ROUTES } from "../../utils/Constant";
import { useTokenInfo } from "../../utils/ReactHooks";
import { useTeamFetcher } from "../../utils/TeamsFetcherHooks";
import { useProjectFetcher } from "../../utils/ProjectsFetcherHooks";
import { useDesignFetcher } from "../../utils/DesignsFetcherHooks";
import { useUserInfoFetcher } from "../../utils/UserHooks";
import { getLanguage, setLanguage } from "../../utils/LocaleUtil";
import onboardImage from "../../assets/onboarding/onboarding.jpg"; // Tell Webpack this JS file uses this image
import EmailOnboard from "../../components/Onboarding/EmailOnboard";
import SignUp from "../../components/Onboarding/SignUp";
import AboutProject from "../../components/Onboarding/AboutProject";

const OnboardingContainer = (props) => {
  const { match: { params: { token } = {} } = {} } = props;

  //Change the language if the URL param set a specific language
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();

  const lang = useMemo(() => {
    const query = new URLSearchParams(location.search);
    let lang = query.get("lang");
    if (lang) {
      setLanguage(lang);
    } else {
      lang = getLanguage();
    }

    return lang;
  }, [location.search]);

  //Query parameter campaign (use for analytics)
  const campaign = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("campaign");
  }, [location.search]);

  //About Project, for who
  const FOR_WHO = Object.freeze({
    COMPANY: "company",
    STUDENT: "student",
    PERSONAL: "personal",
  });

  const forWhoOptions = useMemo(
    () =>
      Object.values(FOR_WHO).map((who) => {
        return {
          value: who,
          label: t(`onboard.for_who.${who}`),
          disabled: false,
        };
      }),
    [FOR_WHO, t]
  );

  //About Project, project sizes

  const PROJECT_SIZE = Object.freeze({
    LESS_20: "0-19",
    LESS_50: "20-49",
    LESS_150: "50-149",
    LESS_500: "150-499",
    LESS_5000: "500-4999",
    MORE_5000: "5000-",
  });

  const projectSizeOptions = useMemo(
    () =>
      Object.values(PROJECT_SIZE).map((projectSize) => {
        return {
          value: projectSize,
          label: t(`onboard.project_size.${projectSize}`),
          disabled: false,
        };
      }),
    [PROJECT_SIZE, t]
  );

  //Onboarding steps
  const ONBOARDING_STEPS = Object.freeze({
    EMAIL: "email",
    LOGIN: "login",
    SIGNUP: "signup",
    ABOUT_PROJECT: "about",
  });
  const [currentStep, setCurrentStep] = useState(ONBOARDING_STEPS.EMAIL);

  const { fetchMyTeamsAndUpdateStore } = useTeamFetcher();
  const { fetchMyProjectsAndUpdateStore } = useProjectFetcher();
  const { fetchMyDesignsAndUpdateStore } = useDesignFetcher();
  const { initUserInfo } = useUserInfoFetcher();

  const [onboardingInfo, setOnboardingInfo] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    optNewsletter: true,
    language: "",
    forWho: "",
    projectSize: "",
    teamName: "",
    eventDate: "",
    campaign: "",
  });

  //Campaign url query parameter may not always be set
  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      campaign: campaign || "",
    }));
  }, [campaign, setOnboardingInfo]);

  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      language: getLanguage(),
    }));
  }, [setOnboardingInfo]);

  const [errorMsg, setErrorMsg] = useState("");
  const tokenInfo = useTokenInfo(token);

  /**
   *
   */
  const onCheckEmailNextStep = useCallback(
    async (e, email) => {
      e.preventDefault();

      //Check if user exits, if exists send to login step, if doesn't exists, send to signup step
      const userExists = await AuthService.userExists(onboardingInfo.email)
        .then(() => {
          return true;
        })
        .catch((e) => {
          return false;
        });
      if (userExists) {
        setCurrentStep(ONBOARDING_STEPS.LOGIN);
      } else {
        setCurrentStep(ONBOARDING_STEPS.SIGNUP);
      }

      //Reset error message
      setErrorMsg("");
    },
    [setCurrentStep, ONBOARDING_STEPS, onboardingInfo]
  );

  /**
   *
   */
  const onBackToEmailStep = useCallback(
    async (e) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.EMAIL);
    },
    [setCurrentStep, ONBOARDING_STEPS]
  );

  /**
   *
   */
  const onBackToUserInfoStep = useCallback(
    async (e) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.SIGNUP);
    },
    [setCurrentStep, ONBOARDING_STEPS]
  );

  /**
   *
   * @param {*} e
   * @param {*} email
   * @param {*} password
   * @returns
   */
  const onSignInClick = async (e, email, password) => {
    e.preventDefault();

    try {
      const res = await AuthService.login({
        email,
        password,
        inviteToken: _.isEmpty(token) ? null : token,
      }).catch((error) => {
        setErrorMsg(t("sign_in." + error.response.data.message));
        return { data: error.response.data };
      });

      if (res.data.status === "success") {
        AuthService.setLocalUser(
          res.headers["x-access-token"],
          res.data.data.user.id.toString(),
          res.data.data.user.email
        );

        initUserInfo();

        window.analytics.track("Signed In");

        //if no invite token, then route to the user's projects
        if (!token) {
          history.push(ROUTES.HOME);
          return;
        }

        const teamId = res.data.data.joinedTeam.id;

        //If there is an invite token, get the teamId associated and route the user to the tean's page.
        // const {
        //   data: { data: teamId },
        // } = await TeamService.joinUserToTeamWithInviteToken(token);

        //Since we added the user to the team, we must refresh the teams's store
        fetchMyTeamsAndUpdateStore();
        fetchMyProjectsAndUpdateStore();
        fetchMyDesignsAndUpdateStore();

        history.push(
          routeFormatter(ROUTES.TEAM_PROJECTS, { params: { id: teamId } })
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Last step
   */
  const onSignUpGetStarted = useCallback(
    async (e) => {
      e.preventDefault();

      //Dates can't be an empty string, so we must set eventDate to null if it is ""
      onboardingInfo.eventDate = _.isEmpty(onboardingInfo.eventDate)
        ? null
        : onboardingInfo.eventDate;

      onboardingInfo.inviteToken = _.isEmpty(token) ? null : token;

      //Do the .catch in the signUp call instead of a big try catch
      //On failure of the API (like 400 missing parameters),
      //Axios throws an error that must be catched
      const res = await AuthService.signUp(onboardingInfo).catch((error) => {
        return error.response;
      });

      const { status, data, message } = res.data;

      if (status !== "success") {
        if (message === "username_already_exists") {
          setErrorMsg(t("sign_up." + message, { email: onboardingInfo.email }));
          return;
        }
        setErrorMsg(t("sign_up." + message));
        return;
      }

      const { newUser, newProject, joinedTeam } = data;

      //Equivalent of login right after signup
      AuthService.setLocalUser(
        res.headers["x-access-token"],
        newUser.id.toString(),
        newUser.email,
        onboardingInfo.optNewsletter
      );

      initUserInfo();

      //Analytics
      if (
        _.isEmpty(onboardingInfo.forWho) ||
        _.isEmpty(onboardingInfo.projectSize)
      ) {
        window.analytics.track("Account Created", {
          invited: token ? true : false, // whether the user signed up via an invitation link
        });
      } else {
        window.analytics.track("Account Created", {
          invited: token ? true : false, // whether the user signed up via an invitation link
          forWho: onboardingInfo.forWho,
          projectSize: onboardingInfo.projectSize,
          label: `${onboardingInfo.forWho} / ${onboardingInfo.projectSize}`,
          campaign: onboardingInfo.campaign,
        });
      }

      ///if no invite token, then route to the newly created project and popup the create design
      if (!token) {
        history.push(
          routeFormatter(ROUTES.DESIGNS_SHOW_NEW, {
            params: { id: newProject.id },
          })
        );
        return;
      }
      // //If there is an invite token, get the teamId associated and route the user to the tean's page.
      // const {
      //   data: { data: teamId },
      // } = await TeamService.joinUserToTeamWithInviteToken(token);

      //Since we added the user to the team, we must refresh the teams's store (team, project)
      fetchMyTeamsAndUpdateStore();
      fetchMyProjectsAndUpdateStore();
      fetchMyDesignsAndUpdateStore();

      //Push to joined team's page
      history.push(
        routeFormatter(ROUTES.TEAM_PROJECTS, { params: { id: joinedTeam.id } })
      );
    },
    [
      setErrorMsg,
      onboardingInfo,
      fetchMyDesignsAndUpdateStore,
      fetchMyProjectsAndUpdateStore,
      fetchMyTeamsAndUpdateStore,
      initUserInfo,
      t,
      history,
      token,
    ]
  );

  /**
   *
   */
  const onCheckUserInfoNextStep = useCallback(
    async (e) => {
      e.preventDefault();

      //TODO some validation?

      //If the user was invited, we don't ask for the project and team info (since the user is joining a team...)
      //A default Team and project will still be created though...
      if (tokenInfo) {
        onSignUpGetStarted(e);
        return;
      }
      setCurrentStep(ONBOARDING_STEPS.ABOUT_PROJECT);
    },
    [setCurrentStep, ONBOARDING_STEPS, onSignUpGetStarted, tokenInfo]
  );

  return (
    <React.Fragment>
      <Helmet>
        <title>Jameo Studio</title>
        <meta charSet="utf-8" />
        <meta name="description" content={t("sign_in.description")} />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.leftContent}>
          <div className={styles.topBar}>
            <div className={styles.logoDiv}>
              <JameoLogoMedium className={styles.logo} />
            </div>
            <Link
              to={
                token
                  ? routeFormatter(ROUTES.LOGIN_WITH_TOKEN, {
                      params: { token },
                      query: {
                        lang: lang === null || lang === "en" ? "fr" : "en",
                      },
                    })
                  : routeFormatter(ROUTES.LOGIN, {
                      query: {
                        lang: lang === null || lang === "en" ? "fr" : "en",
                      },
                    })
              }
            >
              {t("sign_in.language")}
            </Link>
          </div>

          {tokenInfo && (
            <div className={styles.invitationContainer}>
              <span className={styles.invitationTitle}>
                {t("sign_in.invitation", {
                  name: tokenInfo.invitorName,
                })}
              </span>
              <span className={styles.invitationTeamName}>
                {tokenInfo.teamName}
              </span>
              .
            </div>
          )}

          <div className={styles.stepContainer}>
            {currentStep === ONBOARDING_STEPS.EMAIL && (
              <EmailOnboard
                onNextStep={onCheckEmailNextStep}
                email={onboardingInfo.email}
                setEmail={(email) =>
                  setOnboardingInfo((infos) => ({
                    ...infos,
                    email,
                  }))
                }
              />
            )}
            {currentStep === ONBOARDING_STEPS.LOGIN && (
              <Login
                onNextStep={onSignInClick}
                onPreviousStep={onBackToEmailStep}
                email={onboardingInfo.email}
              />
            )}
            {currentStep === ONBOARDING_STEPS.SIGNUP && (
              <SignUp
                onNextStep={onCheckUserInfoNextStep}
                onPreviousStep={onBackToEmailStep}
                userInfo={onboardingInfo}
                setUserInfo={setOnboardingInfo}
              />
            )}
            {currentStep === ONBOARDING_STEPS.ABOUT_PROJECT && (
              <AboutProject
                onNextStep={onSignUpGetStarted}
                onPreviousStep={onBackToUserInfoStep}
                aboutInfo={onboardingInfo}
                setAboutInfo={setOnboardingInfo}
                forWhoOptions={forWhoOptions}
                projectSizeOptions={projectSizeOptions}
              />
            )}
          </div>

          <div className={styles.errorContainer}>
            {errorMsg && <p className={styles.error}>{errorMsg}</p>}
          </div>
        </div>
        <div className={styles.rightContent}>
          <img src={onboardImage} alt="Logo" />;
        </div>
      </div>
    </React.Fragment>
  );
};

export default OnboardingContainer;
